<script>
import CandidateImage from '@/components/candidate/candidate-image.vue'
import CandidatePreferredButton from '@/components/candidate/candidate-preferred-button.vue'
import accessibleNavigationMixin from '@/mixins/accessibleNavigationMixin'
import candidateMixin from '@/mixins/candidateMixin'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'

export default {
  name: 'CandidateCard',
  components: { CandidateImage, CandidatePreferredButton },
  mixins: [candidateMixin, accessibleNavigationMixin],
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    tabindex: {
      type: [Number, String],
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      VuetifyTextClass,
    }
  },
  computed: {
    fullName() {
      return `${this.candidate.firstName} ${this.candidate.lastName}`.trim()
    },
    preferredCount() {
      if (typeof this.candidate.preferredLocationCount === 'number') {
        return this.candidate.preferredLocationCount
      }

      return (this.candidate.preferredLevels ?? []).reduce(
        (sum, level) => sum + (level?.linkCount ?? 0),
        0
      )
    },
    preferredSummary() {
      if (typeof this.candidate.accessibleLocationCount === 'number') {
        if (this.preferredCount === 0) {
          return this.$t('candidateList.card.notCurrentlyPreferred')
        }

        if (
          this.candidate.accessibleLocationCount > 0 &&
          this.preferredCount === this.candidate.accessibleLocationCount
        ) {
          return this.$t('candidateList.card.preferredAtAllLocations', {
            count: this.candidate.accessibleLocationCount,
          })
        }

        return this.$t('candidateList.card.preferredAtLocations', {
          preferred: this.preferredCount,
          total: this.candidate.accessibleLocationCount,
        })
      }

      return this.$t('candidateList.card.preferredBy', {
        count: this.preferredCount,
      })
    },
  },
  methods: {
    viewCandidateDetails() {
      this.$emit('view-candidate', this.candidate.candidateId)
    },
  },
}
</script>

<template>
  <v-card
    class="candidate-card custom-elevation d-flex rounded-lg"
    elevation="0"
    height="100%"
    :aria-label="
      $t('candidateList.card.viewProfileAriaLabel', { name: fullName })
    "
    :tabindex="tabindex"
    @click="viewCandidateDetails"
    @keydown="onAccNavActionKeydown($event, viewCandidateDetails)"
  >
    <v-card-text class="d-flex pa-4">
      <div class="candidate-card__avatar-slot flex-shrink-0 mr-4">
        <v-lazy>
          <CandidateImage
            :id="candidate.candidateId"
            :full-name="fullName"
            size="90"
            :font-size="22"
            rounded
          />
        </v-lazy>
      </div>

      <div class="flex-grow-1 candidate-card__body">
        <div class="d-flex align-center">
          <h4
            class="candidate-card__name flex-grow-1 ma-0"
            :class="VuetifyTextClass.smallBodyBold"
          >
            {{ fullName }}
          </h4>
          <CandidatePreferredButton
            :candidate="candidate"
            :disabled="disabled"
            @preference-updated="$emit('preference-updated')"
          />
        </div>

        <div :class="[VuetifyTextClass.smallBody, 'candidate-card__line']">
          {{
            $t('candidateList.card.bookingCount', {
              count: candidate.numberOfBookings,
            })
          }}
        </div>
        <div :class="[VuetifyTextClass.smallBody, 'candidate-card__line']">
          {{
            $t('candidateList.card.lastBooking', {
              date: displayDate(candidate.mostRecentBookingDateLocal),
            })
          }}
        </div>
        <div :class="[VuetifyTextClass.smallBody, 'candidate-card__line']">
          {{ preferredSummary }}
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" src="@/styles/candidate/candidate-card.scss" />
